// =====================================================================================================================
// ========== COMPANIES TABLE
//
// Rationale:
//  • The table owns its own data-fetching so Companies.js stays minimal.
//  • We use Supabase's nested select syntax ("company_owner(...)") instead of a raw SQL join.
//    This avoids duplicate company rows and works without a database view.
//  • Owners are aggregated into a display string in JS (same logic as the original STRING_AGG query).
//  • The DataGrid `loading` prop activates its built-in skeleton overlay — no extra component needed.
//  • `refreshTrigger` (incremented by the parent after any mutation) is the only dep that causes a re-fetch,
//    keeping the fetch count predictable.
//  • A cancel flag (`cancelled`) prevents stale state when the component unmounts mid-fetch or when
//    refreshTrigger changes rapidly.
// =====================================================================================================================

import React, { useState, useEffect, useCallback } from "react"
import Alert from "@mui/material/Alert"
import { DataGrid } from "@mui/x-data-grid"

// ── HELPERS ───────────────────────────────────────────────────────────────────────────────────────────────────────────

/** Turn a company_owner join row into a short display string for the table. */
function ownerLabel(owner) {
    if (owner.owner_person) {
        const { first_name, last_name } = owner.owner_person
        const initial = first_name ? `${first_name[0]}. ` : ""
        return `${initial}${last_name ?? ""}`.trim()
    }
    if (owner.owner_company) {
        return owner.owner_company.name ?? ""
    }
    return ""
}

/** Parse a Supabase error into a human-readable French message. */
function parseError(err) {
    if (!err) return null
    if (err.code === "23503") return "Impossible de supprimer : des données liées existent encore."
    if (err.code === "23505") return "Une entreprise identique existe déjà."
    return err.message ?? "Une erreur inattendue s'est produite."
}

// ── COLUMNS ───────────────────────────────────────────────────────────────────────────────────────────────────────────

const COLUMNS = [
    { field: "name",   headerName: "Nom",          flex: 2 },
    { field: "owners", headerName: "Propriétaires", flex: 3 },
    { field: "status", headerName: "Statut",        flex: 1 },
]

// ── COMPONENT ─────────────────────────────────────────────────────────────────────────────────────────────────────────

export default function CompaniesTable({ supabase, selectedCompanyId, setSelectedCompanyId, refreshTrigger }) {

    const [rows,      setRows]      = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [error,     setError]     = useState(null)

    // Re-fetch whenever the parent signals a mutation has completed.
    useEffect(() => {
        let cancelled = false
        const fetchData = async () => {
            setIsLoading(true)
            setError(null)
            try {
                const { data, error: fetchError } = await supabase
                    .from("company")
                    .select(`
                        id,
                        name,
                        status,
                        company_owner!company_id (
                            shares,
                            owner_company_id,
                            owner_person_id,
                            owner_person:person!owner_person_id ( id, first_name, last_name ),
                            owner_company:company!owner_company_id ( id, name )
                        )
                    `)
                    .order("name")

                if (fetchError) throw fetchError

                if (!cancelled) {
                    // Flatten nested owners into a comma-separated display string
                    const formattedRows = (data ?? []).map((company) => ({
                        id:     company.id,
                        name:   company.name,
                        status: company.status,
                        owners: (company.company_owner ?? [])
                            .map(ownerLabel)
                            .filter(Boolean)
                            .join(", "),
                    }))
                    setRows(formattedRows)
                }
            } catch (err) {
                if (!cancelled) setError(parseError(err))
            } finally {
                if (!cancelled) setIsLoading(false)
            }
        }

        fetchData()
        return () => { cancelled = true }
    }, [supabase, refreshTrigger])

    // ── SELECTION ────────────────────────────────────────────────────────────────────────────────────────────────────
    // Keep the DataGrid selection model in sync with the parent's selectedCompanyId.
    // When the user clicks a row that is already selected (i.e. the checkbox deselects it), we pass null.

    const handleSelectionChange = useCallback(
        (model) => {
            setSelectedCompanyId(model.length ? model[0] : null)
        },
        [setSelectedCompanyId]
    )

    // ── RENDER ───────────────────────────────────────────────────────────────────────────────────────────────────────

    if (error) return <Alert severity="error">{error}</Alert>

    return (
        <DataGrid
            autoHeight
            checkboxSelection
            disableMultipleRowSelection
            loading={isLoading}
            rows={rows}
            columns={COLUMNS}
            rowSelectionModel={selectedCompanyId ? [selectedCompanyId] : []}
            onRowSelectionModelChange={handleSelectionChange}
            getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
            }
            initialState={{
                pagination: { paginationModel: { pageSize: 20 } },
            }}
            pageSizeOptions={[10, 20, 50]}
            density="compact"
        />
    )
}